import React, { useEffect } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadEmployeeNotifications } from '../../employees/actions/load-employee-notifications';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { userSelector } from '../../reducers/current-user-selectors';
import {
  employeeForJobApplicantIdSelector,
  employeeNotificationsForOrgIdSelector,
  employeesStateForOrgIdAndAllStatusSelector,
} from '../../reducers/employee-selectors';
import { RequestState } from '../../reducers/utils/state-manager';

import { UiDialog } from '../../ui/ui-dialog';

import { EmployedDialogContent } from './employed-dialog-content';

import { VALIDATING_STATUS } from '../../config/application-status-config';
import { EMPLOYEE_PERMISSION } from '../../util/permissions';
import { LoadingMessage } from '../../widgets/loading-message';

const mapStateToProps = (state: any, { application }: InputProps) => ({
  user: userSelector(state),
  employee: employeeForJobApplicantIdSelector(state, application.job_applicant_id),
  employeeStates: employeesStateForOrgIdAndAllStatusSelector(state, application.orgId),
  notifications: employeeNotificationsForOrgIdSelector(state, application.orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { application: { orgId } }: InputProps,
) => ({
  actions: {
    loadEmployeeNotifications: () => dispatch(loadEmployeeNotifications(orgId)),
    loadOrgEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
  },
});

type InputProps = {
  close: () => void;
  application: FullApplicant;
  newStatus: ApplicationStatus;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EmployedDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const { close, user, employee, notifications, actions, application, newStatus, employeeStates } =
    props;

  useEffect(() => {
    if (user.employeesPermission === EMPLOYEE_PERMISSION.ALL) {
      actions.loadOrgEmployees();
      actions.loadEmployeeNotifications();
    }
  });

  const { hireStartDateTime } = application;
  const startDateSetInFuture =
    newStatus === VALIDATING_STATUS &&
    hireStartDateTime &&
    moment(hireStartDateTime).isAfter(moment());
  // This is an important line - it prevents showing this dialog when the start date is rescheduled
  if (user.employeesPermission !== EMPLOYEE_PERMISSION.ALL || startDateSetInFuture) {
    return null;
  }
  const employeesAreLoading = employeeStates.every(
    (requestState: RequestState) => !requestState || requestState.loading,
  );
  if (!employeesAreLoading && !employee) {
    return null;
  }

  if (!employee || !notifications) {
    return (
      <UiDialog title="Congratulations!" onClose={close}>
        <UiDialog.Content>
          <LoadingMessage />
        </UiDialog.Content>
      </UiDialog>
    );
  }

  return (
    <EmployedDialogContent
      application={application}
      employee={employee}
      notifications={notifications}
      close={close}
    />
  );
});
